/**
 * Shared fetch client for Elreign backend calls.
 *
 * Reads the current Supabase access token on every call and attaches it as
 * a Bearer header. Throws a typed ApiError on non-2xx; consumers (mostly
 * TanStack Query hooks) read `.status` and `.code` to drive retry policy +
 * step-up redirect interceptor.
 *
 * Lifted from the two inline duplicates that previously lived in
 * `components/dashboard/hero.tsx` and `components/screens/resolution-sign.tsx`.
 * New code MUST go through here so 401 / 403-STEP_UP_REQUIRED / network
 * handling stays in one place.
 */

import { supabase } from '../supabase';
import { useImpersonationStore } from '../impersonation-store';
import { useFirmActStore } from '../firm-act-store';
import { bearerTokenForContext } from '../acting/bearer';
import { isOperatorSessionOnlyPath } from './gateway-tiers';

/**
 * Error thrown when the backend returns a non-2xx response. `status` is the
 * HTTP code (the QueryClient retry policy keys on this); `code` is the
 * backend's machine-readable error code from the JSON body (e.g.
 * `RESOLUTION_NOT_FOUND`, `STEP_UP_REQUIRED`, `NO_TENANT_ASSIGNMENT`).
 */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string | undefined;
  readonly body: unknown;
  readonly redirectTo: string | undefined;

  constructor(
    status: number,
    body: unknown,
    code: string | undefined,
    redirectTo: string | undefined,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
    this.code = code;
    this.redirectTo = redirectTo;
  }

  /**
   * True when the backend signalled a fresh step-up challenge is required AND
   * a challenge could actually satisfy it.
   *
   * The second half was added 2026-08-27 and it is the important one.
   *
   * `mintFirmActToken` never stamps `mfa_challenge_at` and the minted token
   * carries no `session_id`, so on an act-as or impersonation credential the
   * gate refuses BY CONSTRUCTION and no ceremony can change that. Until today
   * the client could not tell, so every one of the ~20 call sites below opened
   * the modal, the operator typed a correct code, and the replayed write 403'd
   * again with "your security confirmation expired before the action
   * completed" — which is false, and reads as a success followed by an
   * unexplained failure.
   *
   * ARC-01 T2 made the ceremony itself complete on the operator's own session,
   * which removed the 403 in the MIDDLE of that loop and left the one at the
   * end. That was a half fix, and a half fix here is worse than none: a fast
   * honest refusal became a slow dishonest one.
   *
   * `requireStepUp` now returns `stepUpPossible` on the 403 body. When it is
   * false the caller falls through to its normal error path and shows the
   * server's own sentence, which no longer tells the user to re-authenticate.
   * Whether any of those walls should OPEN for a firm is Paul's ruling, not a
   * client-side decision; this only stops the app lying about why it refused.
   */
  get isStepUpRequired(): boolean {
    if (this.code !== 'STEP_UP_REQUIRED') return false;
    return this.stepUpPossible !== false;
  }

  /**
   * What the server said about whether a challenge can succeed at all.
   * `undefined` from an older backend, which is read as "assume it can" so a
   * version skew fails the way it always did rather than a new way.
   */
  get stepUpPossible(): boolean | undefined {
    const body = this.body as { stepUpPossible?: unknown } | null | undefined;
    return typeof body?.stepUpPossible === 'boolean' ? body.stepUpPossible : undefined;
  }

  /** `self` | `firm-act-as` | `impersonation`, when the server said. */
  get actingPosture(): string | undefined {
    const body = this.body as { actingPosture?: unknown } | null | undefined;
    return typeof body?.actingPosture === 'string' ? body.actingPosture : undefined;
  }
}

export interface ApiRequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  /** JSON-serialised into the body. Use `undefined` for body-less requests. */
  body?: unknown;
  /** AbortSignal — wired through from TanStack Query's `signal`. */
  signal?: AbortSignal;
  /** Override the default content-type when needed (rare). */
  contentType?: string;
  /** Extra request headers merged on top of the defaults (Authorization +
   *  content-type). Used for money-mutation idempotency: the caller passes
   *  `{ 'idempotency-key': <uuid> }` so a double-submit replays server-side
   *  rather than issuing a second charge. */
  headers?: Record<string, string>;
  /**
   * Force the operator's own Supabase session even when an impersonation is
   * active (A2). Used by the impersonate/stop call, which is super_admin +
   * step-up gated and would 403 if it travelled on the impersonation JWT.
   */
  useOperatorSession?: boolean;
  /**
   * AC4 firm-console compliance writes: ride a SPECIFIC client's act-as token
   * for THIS ONE call, without flipping the whole console into act-as posture
   * (the /admin console clears the global firm-act context on entry). The
   * compliance screens (EDD open, etc.) mint a per-client act-as token via
   * POST /v1/firm/clients/{clientTenantId}/act-as and pass it here so the write
   * lands in the picked trust's CLIENT tenant. Grant-gated server-side: a
   * `manage` grant mints an 'admin' writer token; a `read` grant mints a
   * write-denied 'firm-reader' token (the write 403s) — never a silent
   * wrong-tenant write. Ignored when `useOperatorSession` is set.
   */
  actAsToken?: string;
}

/**
 * Resolves the API base URL for cross-origin calls to APIM.
 *
 * History: previously the frontend called relative paths like `/api/v1/...`
 * and relied on the Vite dev proxy + SWA `routes.rewrite` to route to APIM.
 * That works in dev (Vite proxy) but NOT in prod SWA: SWA Standard's
 * `routes.rewrite` to an external URL requires `backends link`, which
 * rejects APIM/FA backends in southafricanorth because SWA only links
 * backends in westus2/centralus/eastus2/westeurope/eastasia (POPIA region
 * isn't supported). So we make absolute APIM calls cross-origin instead.
 *
 * Build-time env var `VITE_API_BASE_URL` is injected per env:
 *   dev      -> https://apim-elreign-dev.azure-api.net
 *   staging  -> https://apim-elreign-staging.azure-api.net
 *   prod     -> https://apim-elreign-prod.azure-api.net
 *
 * When unset (e.g., `pnpm dev` local development), defaults to relative
 * paths so the Vite proxy can route to the dev FA directly.
 *
 * CORS is configured on APIM globally to allow the SWA origins and
 * the Authorization + Content-Type headers used here.
 */
const API_BASE_URL = (import.meta.env['VITE_API_BASE_URL'] as string | undefined) ?? '';

/**
 * Coalesce concurrent access-token reads.
 *
 * On a cold dashboard load the app fires ~8 queries at once (trusts, holdings,
 * allocation, distributions, trust-summary, summary, resolutions, notifications,
 * me, branding). Each one calls `supabase.auth.getSession()`, which takes the
 * client's internal auth lock and does an expiry/refresh check — so N parallel
 * requests each pay that cost and partially serialise on the lock.
 *
 * This shares ONE in-flight `getSession()` promise across all callers that need
 * the token in the same tick, then clears it the moment it settles. It is NOT a
 * TTL cache: the token is never held beyond the concurrent batch, so supabase's
 * own refresh/expiry logic still runs afresh on the next batch — token refresh
 * and expiry handling are completely unchanged. The only thing removed is the
 * redundant N-way lock contention within a single burst of requests.
 */
let inFlightToken: Promise<string | undefined> | null = null;

function readAccessToken(): Promise<string | undefined> {
  if (inFlightToken) return inFlightToken;
  const p = supabase.auth
    .getSession()
    .then(({ data }) => data.session?.access_token)
    .finally(() => {
      // Clear as soon as this read settles so the NEXT batch does a fresh
      // getSession() (honouring supabase's expiry/refresh check). Never cache
      // a token across batches.
      inFlightToken = null;
    });
  inFlightToken = p;
  return p;
}

/**
 * Issue a JSON request against the Elreign backend.
 *
 * @param path absolute path beginning with `/api/v1/...`. When
 *             `VITE_API_BASE_URL` is set, prepended to form an absolute
 *             URL pointed at APIM; otherwise stays relative (Vite proxy
 *             in local dev).
 *
 * @throws ApiError on non-2xx (parses JSON body if available; falls back
 *         to text if the response isn't JSON).
 */
export async function api<T = unknown>(
  path: string,
  opts: ApiRequestOptions = {},
): Promise<T> {
  // A2: when an impersonation is active, data calls travel on the impersonation
  // JWT so the operator sees the target's tenant. First-class tenancy: when a
  // firm is "administering" a client, data calls travel on the act-as token so
  // the firm-user sees the CLIENT's tenant. Both opt out via `useOperatorSession`
  // — the impersonate/stop + the firm-console calls (Client Book, onboarding)
  // must run on the operator's own session.
  // AC4: an explicit per-call client-scoped act-as token (a firm-console
  // compliance write into a picked client trust) takes precedence over every
  // ambient token source — it targets ONE client tenant for ONE call and must
  // not be shadowed by the global firm-act store or the operator session.
  // ARC-01 T4: WHICH TOKEN GOES ON THE WIRE IS NOW ONE QUESTION, ASKED ONCE.
  // This block used to call four store accessors — `activeImpersonationToken`,
  // `firmActStatus`, `activeFirmActToken` and `readAccessToken` — and re-derive
  // the posture from their answers. That derivation existed in five places in
  // this frontend and disagreed with itself in three of them (survey §3), which
  // is the defect ARC-01 exists to close. It now asks
  // `bearerTokenForContext()` once.
  //
  // Two things stay HERE and are deliberately not context's business:
  //   · The per-call overrides. `useOperatorSession` and `actAsToken` are
  //     properties of the CALL, not of who is acting, so they are resolved
  //     before the context is ever consulted — which also preserves the
  //     assertion that an explicit per-client token never reads the operator
  //     session at all (`act-as-expiry.test.ts`: `getSession` not called).
  //   · The ACT_AS_EXPIRED throw and its `/admin/client-book` redirect, kept
  //     verbatim, because `use-act-as-renewal.ts` keys on that exact code as
  //     proof the request never left the browser and is safe to retry.
  const explicitActAsToken = opts.useOperatorSession ? null : (opts.actAsToken ?? null);
  let token: string | undefined;
  /** True when the token about to be sent is a standing-in credential (a firm
   *  act-as or a platform impersonation), not the operator's own session. */
  let standingIn = false;
  if (opts.useOperatorSession) {
    // Shared in-flight read: N concurrent dashboard queries dedupe onto one
    // getSession() call instead of each independently taking the auth lock.
    // See readAccessToken — this does not cache across request batches, so
    // token refresh/expiry is unaffected.
    token = await readAccessToken();
  } else if (explicitActAsToken) {
    token = explicitActAsToken;
    standingIn = true;
  } else {
    let bearer = await bearerTokenForContext(readAccessToken);
    if (bearer.credential.kind === 'impersonation' && bearer.credential.status === 'expired') {
      // PARITY, AND IT IS LOAD-BEARING. Reading the impersonation token used to
      // CLEAR an expired one as a side effect of being read
      // (`impersonation-store.ts:57-58`), and that side effect is the ONLY thing
      // in the app that ever un-sticks an expired impersonation — the banner
      // clears only on an explicit Stop. `bearerTokenForContext` deliberately
      // does not clear (it is a read), so the clear lives here, and the context
      // is re-derived afterwards so an active firm-act underneath still wins,
      // exactly as it did when these were four separate accessors.
      //
      // ⚠ This preserves today's behaviour on purpose: an expired impersonation
      // falls back to the operator's own session rather than refusing. That is
      // NOT what `bearerTokenForContext` would do on its own, and it is the
      // opposite of the ruling already taken for the beneficiary CSV
      // (`beneficiaries.test.ts:287` — "refuses rather than downgrading to the
      // operator session"). Making the two agree is a behaviour change with a
      // security argument behind it and belongs in its own tranche with its own
      // test, not as a silent ride-along on a refactor. Raised for Paul.
      useImpersonationStore.getState().clear();
      bearer = await bearerTokenForContext(readAccessToken);
    }
    if (bearer.credential.kind === 'act-as' && bearer.credential.status === 'expired') {
      // The firm was administering a client but the 15-min act-as token expired
      // mid-flow. Do NOT fall back to the operator's own session — that would route
      // this (possibly WRITE) call into the FIRM's tenant instead of the client's.
      // Clear the stale context and fail loudly so the caller re-opens the client.
      useFirmActStore.getState().clear();
      throw new ApiError(
        401,
        { code: 'ACT_AS_EXPIRED' },
        'ACT_AS_EXPIRED',
        '/admin/client-book',
        'Your access to this client expired. Reopen the client to continue.',
      );
    }
    token = bearer.token ?? undefined;
    standingIn = bearer.credential.kind !== 'session';
  }

  // A CALL THAT CAN ONLY FAIL IS NOT SENT (NEW-R12-MAPSTRUCTURE-ACTAS-401).
  // The gateway answers /v1/admin, /v1/superadmin and /v1/partner on the
  // operator's own Supabase session and nothing else (see ./gateway-tiers.ts),
  // so on a standing-in token it is a 401 before any handler runs. Entering a
  // client through Map structure sent two of them from the still-mounted firm
  // console. The screens decline those reads while standing in; this is the
  // floor under every screen that does not. 403 because it is a refusal about
  // who is asking, which the retry policy never retries.
  if (standingIn && isOperatorSessionOnlyPath(path)) {
    throw new ApiError(
      403,
      { code: 'OPERATOR_SESSION_ONLY' },
      'OPERATOR_SESSION_ONLY',
      undefined,
      'This is part of your firm console and cannot be opened from inside a client space.',
    );
  }

  const headers: Record<string, string> = {};
  if (opts.body !== undefined) {
    headers['content-type'] = opts.contentType ?? 'application/json';
  }
  if (token) {
    headers['authorization'] = `Bearer ${token}`;
  }
  // Caller-supplied headers last so an explicit idempotency-key can't be
  // clobbered, but they cannot override the auth header (auth is applied
  // above from the live session and is the security boundary).
  if (opts.headers) {
    for (const [k, v] of Object.entries(opts.headers)) {
      if (k.toLowerCase() === 'authorization') continue;
      headers[k] = v;
    }
  }

  const url = API_BASE_URL ? `${API_BASE_URL}${path}` : path;
  const res = await fetch(url, {
    method: opts.method ?? 'GET',
    headers,
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
    signal: opts.signal,
  });

  if (!res.ok) {
    let body: unknown = null;
    let code: string | undefined;
    let redirectTo: string | undefined;
    try {
      body = await res.json();
      if (body && typeof body === 'object') {
        const obj = body as Record<string, unknown>;
        // Backend error envelopes use either `code` (standard) or `errorCode`
        // (the step-up middleware variant per backend/src/lib/middleware/step-up.ts).
        if (typeof obj['code'] === 'string') code = obj['code'];
        else if (typeof obj['errorCode'] === 'string') code = obj['errorCode'];
        if (typeof obj['redirectTo'] === 'string') redirectTo = obj['redirectTo'];
      }
    } catch {
      try {
        body = await res.text();
      } catch {
        body = null;
      }
    }
    throw new ApiError(
      res.status,
      body,
      code,
      redirectTo,
      `API ${opts.method ?? 'GET'} ${url} failed: ${res.status}` +
        (code ? ` ${code}` : ''),
    );
  }

  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}
